import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Center,
  CardBody,
  CardHeader,
  Checkbox,
  CircularProgress,
  CircularProgressLabel,
  Container,
  Divider,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Input,
  Link,
  NumberInput,
  NumberInputField,
  Progress,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Switch,
  Text,
  Textarea,
  useToast,
  VStack,
  Badge,
  Wrap,
  WrapItem,
  useBreakpointValue,
} from "@chakra-ui/react";
import { Autocomplete, useLoadScript } from "@react-google-maps/api";
import {
  FiAlertTriangle,
  FiCheck,
  FiChevronRight,
  FiExternalLink,
  FiFile,
  FiMapPin,
  FiRefreshCw,
  FiTrash2,
  FiUpload,
} from "react-icons/fi";
import Header from "./Header";
import { getApiBaseUrl, getPermissionApiBase, fetchApiJson, apiAssetUrl } from "../utils/apiAssetUrl";
import { useNavigate } from "react-router-dom";

const GOOGLE_KEY = "AIzaSyD0AcxoBWhqkMIAfK-F-X6ya3Es-52o1YU";
const LIBRARIES = ["places"];

const PROJECT_TYPES = ["Film", "Web Series", "Ad", "Music Video", "Documentary", "Other"];
const LANGS = ["Hindi", "Gujarati", "English", "Tamil", "Telugu", "Marathi", "Bengali", "Kannada", "Malayalam", "Other"];
const LOCATION_TYPES = ["Public", "Private", "Government"];
const PERMISSION_OPTS = [
  { v: "police", label: "Police Permission" },
  { v: "traffic", label: "Traffic Police" },
  { v: "drone", label: "Drone (DGCA)" },
  { v: "municipal", label: "Municipal Corporation" },
  { v: "fire_noc", label: "Fire NOC" },
  { v: "local_authority", label: "Local Authority / Sarpanch" },
  { v: "asi", label: "ASI (heritage)" },
  { v: "airport_authority", label: "Airport Authority" },
];
const CREW_ROLES = ["Director", "DOP", "Sound", "Art Director", "Production Manager", "Assistant Director", "Makeup", "Costume", "Other"];
const CAMERA_OPTS = ["Cinema", "DSLR", "Drone", "Handheld"];
const VEHICLE_OPTS = ["Vanity Van", "Truck", "Car", "Bike"];

/** Map API / DB values to wizard labels (case and spacing). */
function canonicalizeCameraList(values) {
  const byLower = new Map(CAMERA_OPTS.map((c) => [c.toLowerCase(), c]));
  const out = [];
  const seen = new Set();
  for (const raw of values) {
    const s = String(raw || "").trim();
    if (!s) continue;
    const c = byLower.get(s.toLowerCase());
    if (c && !seen.has(c)) {
      seen.add(c);
      out.push(c);
    }
  }
  return out;
}

function canonicalizeVehicleList(values) {
  const byLower = new Map(VEHICLE_OPTS.map((c) => [c.toLowerCase(), c]));
  const out = [];
  const seen = new Set();
  for (const raw of values) {
    const s = String(raw || "").trim();
    if (!s) continue;
    const c = byLower.get(s.toLowerCase());
    if (c && !seen.has(c)) {
      seen.add(c);
      out.push(c);
    }
  }
  return out;
}
const STEP_LABELS = [
  "Project",
  "Identity",
  "Location",
  "Permissions",
  "Schedule",
  "Crew",
  "Equipment",
  "Vehicles",
  "Special",
  "Utilities",
  "Legal",
  "Final",
];

const STEP_DESCRIPTIONS = [
  "Production details, languages, and synopsis",
  "Verify email and upload identity proofs",
  "Pin the shoot location and site details",
  "Authorities and permits you will need",
  "Shoot window, timing, and night work",
  "Crew size and key roles on set",
  "Camera, gear, lighting, and drone info",
  "Fleet, parking, and entry permissions",
  "Stunts, crowds, sound, and special risks",
  "Power, water, and facilities on location",
  "Indemnity, insurance, and undertaking",
  "Final documents, summary, and submit",
];

/** Shared field chrome — premium, calm contrast */
const inputBaseProps = {
  size: "lg",
  bg: "white",
  color: "gray.900",
  borderColor: "gray.200",
  borderRadius: "lg",
  h: "12",
  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  _hover: { borderColor: "gray.300" },
  _focusVisible: {
    borderColor: "indigo.400",
    boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.22)",
  },
  _placeholder: { color: "gray.500" },
};

const selectBaseProps = {
  ...inputBaseProps,
  iconColor: "gray.400",
  color: "gray.900",
};

const textareaBaseProps = {
  ...inputBaseProps,
  h: "auto",
  minH: "100px",
  py: 3,
};

const numberFieldProps = {
  borderRadius: "lg",
  borderColor: "gray.200",
  h: "12",
  bg: "white",
  color: "gray.900",
  _focusVisible: {
    borderColor: "indigo.400",
    boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.22)",
  },
};

/** Explicit hex + borders — avoids theme overrides that yield white-on-white text or invisible checks. */
const PRIMARY_BG = "#4f46e5";
const PRIMARY_BG_HOVER = "#4338ca";

/** Neutral fill + dark grey text — no white-on-pale issues from global button styles. */
const primaryButtonProps = {
  bg: "gray.200",
  color: "gray.800",
  borderWidth: "2px",
  borderColor: "gray.400",
  _hover: { bg: "gray.300", color: "gray.900", borderColor: "gray.500" },
  _active: { bg: "gray.300", color: "gray.900" },
};

/** Strong checkbox rings + filled checked state (Chakra + global CSS safe). */
const checkboxVisibleProps = {
  sx: {
    ".chakra-checkbox__control": {
      borderRadius: "md",
      borderWidth: "2px",
      borderColor: "gray.500 !important",
      bg: "white !important",
      opacity: "1 !important",
      cursor: "pointer",
      _checked: {
        bg: `${PRIMARY_BG} !important`,
        borderColor: `${PRIMARY_BG_HOVER} !important`,
        color: "white !important",
      },
      _hover: {
        borderColor: "gray.600 !important",
      },
      _disabled: {
        opacity: 0.45,
        cursor: "not-allowed",
      },
    },
    ".chakra-checkbox__label": {
      color: "gray.800 !important",
      cursor: "pointer",
      width: "100%",
      fontSize: "sm",
    },
  },
};

/** Switch thumb/track — strong contrast so the knob isn’t lost on pale tracks. */
const switchVisibleProps = {
  colorScheme: "indigo",
  sx: {
    ".chakra-switch__track": {
      bg: "gray.400 !important",
      _checked: {
        bg: `${PRIMARY_BG} !important`,
      },
    },
    ".chakra-switch__thumb": {
      bg: "white !important",
      borderWidth: "2px",
      borderColor: "gray.600 !important",
      boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
    },
  },
};

const uploadPrimaryButtonProps = {
  as: "label",
  size: "sm",
  cursor: "pointer",
  borderRadius: "lg",
  fontWeight: "semibold",
  bg: "gray.200",
  color: "gray.800",
  _hover: { bg: "gray.300", color: "gray.900" },
};

function FormSection({ title, hint, children }) {
  return (
    <Stack spacing={4}>
      {title ? (
        <Box>
          <Text fontSize="sm" fontWeight="semibold" color="gray.800" letterSpacing="-0.01em">
            {title}
          </Text>
          {hint ? (
            <Text fontSize="xs" color="gray.500" mt={1} lineHeight="tall">
              {hint}
            </Text>
          ) : null}
        </Box>
      ) : null}
      {children}
    </Stack>
  );
}

function StepProgressHeader({ step, total }) {
  const pct = Math.round(((step + 1) / total) * 100);
  const isCompact = useBreakpointValue({ base: true, xl: false });

  return (
    <Stack spacing={4}>
      <Flex
        align={{ base: "flex-start", md: "center" }}
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        gap={4}
      >
        <Box flex={1}>
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color="indigo.600"
            textTransform="uppercase"
            letterSpacing="0.08em"
          >
            Step {step + 1} of {total}
          </Text>
          <Heading
            size={{ base: "md", md: "lg" }}
            mt={1}
            color="gray.900"
            letterSpacing="-0.03em"
            lineHeight="short"
          >
            {STEP_LABELS[step]}
          </Heading>
          <Text fontSize="sm" color="gray.600" mt={2} maxW="lg" lineHeight="tall">
            {STEP_DESCRIPTIONS[step]}
          </Text>
        </Box>
        <Box flexShrink={0} alignSelf={{ base: "center", md: "auto" }}>
          <CircularProgress
            value={pct}
            color={PRIMARY_BG}
            trackColor="gray.200"
            thickness="6px"
            size={{ base: "72px", md: "80px" }}
          >
            <CircularProgressLabel fontSize="sm" fontWeight="bold" color="gray.700">
              {pct}%
            </CircularProgressLabel>
          </CircularProgress>
        </Box>
      </Flex>

      <Box>
        <Progress
          value={pct}
          size="md"
          borderRadius="full"
          bg="gray.300"
          sx={{
            "& > div:first-of-type": {
              bg: PRIMARY_BG,
            },
          }}
        />
        {isCompact ? (
          <Box overflowX="auto" mt={3} mx={{ base: -1, md: 0 }} px={1} css={{ WebkitOverflowScrolling: "touch" }}>
            <HStack as="ol" spacing={2} minW="max-content" pl={0} listStyleType="none" m={0}>
              {STEP_LABELS.map((l, i) => (
                <Badge
                  as="li"
                  key={l}
                  px={2}
                  py={1}
                  borderRadius="full"
                  fontSize="10px"
                  fontWeight="semibold"
                  textTransform="none"
                  bg={
                    i === step
                      ? PRIMARY_BG
                      : i < step
                        ? "indigo.100"
                        : "gray.200"
                  }
                  color={i === step ? "white" : i < step ? "indigo.900" : "gray.700"}
                  opacity={i > step ? 0.85 : 1}
                >
                  {i + 1}. {l}
                </Badge>
              ))}
            </HStack>
          </Box>
        ) : (
          <Flex mt={4} gap={0} align="flex-start" justify="space-between" wrap="nowrap" overflowX="auto">
            {STEP_LABELS.map((l, i) => {
              const done = i < step;
              const current = i === step;
              return (
                <Flex key={l} align="center" flex="1" minW="56px" maxW={`${100 / STEP_LABELS.length}%`}>
                  <Flex direction="column" align="center" flex={1} minW={0}>
                    <Flex
                      align="center"
                      justify="center"
                      w={9}
                      h={9}
                      borderRadius="full"
                      fontSize="xs"
                      fontWeight="bold"
                      flexShrink={0}
                      transition="all 0.2s ease"
                      bg={done ? PRIMARY_BG : current ? "white" : "gray.200"}
                      color={done ? "white" : current ? PRIMARY_BG : "gray.700"}
                      borderWidth={current && !done ? "2px" : done ? "0" : "1px"}
                      borderColor={current && !done ? PRIMARY_BG : done ? "transparent" : "gray.400"}
                      boxShadow={current ? "0 0 0 4px rgba(79, 70, 229, 0.25)" : "none"}
                    >
                      {done ? <Icon as={FiCheck} boxSize={4} color="white" /> : i + 1}
                    </Flex>
                    <Text
                      mt={2}
                      fontSize="10px"
                      fontWeight={current ? "semibold" : "medium"}
                      color={current ? PRIMARY_BG : done ? "gray.800" : "gray.600"}
                      textAlign="center"
                      lineHeight="short"
                      noOfLines={2}
                      w="full"
                    >
                      {l}
                    </Text>
                  </Flex>
                  {i < STEP_LABELS.length - 1 ? (
                    <Box
                      flex={1}
                      minW={2}
                      h="3px"
                      bg={i < step ? PRIMARY_BG : "gray.300"}
                      mt="18px"
                      mx={{ base: 0, xl: 1 }}
                      borderRadius="full"
                      display={{ base: "none", sm: "block" }}
                    />
                  ) : null}
                </Flex>
              );
            })}
          </Flex>
        )}
      </Box>
    </Stack>
  );
}

function emptyForm() {
  return {
    project_title: "",
    project_type: "",
    production_house: "",
    producer_name: "",
    director_name: "",
    line_producer: "",
    language: [],
    description: "",
    applicant_type: "individual",
    contact_number: "",
    email: "",
    email_verified: false,
    documents: { identity: {}, legal: {}, final: {} },
    location_name: "",
    address: "",
    city: "",
    state: "",
    landmark: "",
    location_lat: "",
    location_lng: "",
    location_type: "",
    indoor_outdoor: "outdoor",
    area_usage: { mode: "full", partialDetail: "" },
    permissions_required: [],
    heritage_site: false,
    near_airport_hint: false,
    start_datetime: "",
    end_datetime: "",
    total_days: 0,
    daily_timing: "",
    night_shoot: false,
    crew_size: "",
    crew_details: [{ name: "", role: "" }],
    equipment: { cameraTypes: [], equipmentTags: [], droneDetails: { registrationId: "", pilotName: "" } },
    lighting: "normal",
    generator_required: false,
    vehicles: { count: "", types: [], parkingRequired: false, entryPermission: false },
    special_requirements: {
      loudSound: false,
      roadBlocking: false,
      crowdControl: false,
      stunts: false,
      fireExplosives: false,
      animals: false,
    },
    utilities: { electricity: false, generatorUsage: "", water: "", washroom: "" },
  };
}

function useDebouncedCallback(fn, delay) {
  const t = useRef();
  const fnRef = useRef(fn);
  fnRef.current = fn;
  return useCallback(
    (...args) => {
      if (t.current) clearTimeout(t.current);
      t.current = setTimeout(() => fnRef.current(...args), delay);
    },
    [delay]
  );
}

/** CheckboxGroup + MySQL JSON can yield non-arrays — normalize so Hindi (first item) is not stuck on. */
function toLanguageArray(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  if (typeof raw === "object") return Object.values(raw).map(String).filter(Boolean);
  if (typeof raw === "string") {
    const s = raw.trim();
    if (!s) return [];
    try {
      const j = JSON.parse(s);
      if (Array.isArray(j)) return j.map(String).filter(Boolean);
      if (j && typeof j === "object") return Object.values(j).map(String).filter(Boolean);
    } catch {
      return [s];
    }
  }
  return [];
}

/** permissions_required JSON from API can be an object — never assume array. */
function toPermissionArray(raw) {
  return toLanguageArray(raw);
}

/**
 * Equipment JSON from API may use snake_case, camelCase, or a string. Never read only `cameraTypes`:
 * stale `camera_types` on the same object will fight updates. Output is always camelCase only.
 */
function normalizeEquipmentForState(raw) {
  const base = emptyForm().equipment;
  if (raw == null || raw === "") return { ...base };
  let v = raw;
  if (typeof v === "string") {
    try {
      v = JSON.parse(v);
    } catch {
      return { ...base };
    }
  }
  if (typeof v !== "object" || Array.isArray(v)) return { ...base };
  const droneFrom = v.droneDetails ?? v.drone_details ?? {};
  const drone =
    droneFrom && typeof droneFrom === "object" && !Array.isArray(droneFrom)
      ? { ...base.droneDetails, ...droneFrom }
      : { ...base.droneDetails };
  return {
    cameraTypes: canonicalizeCameraList(toLanguageArray(v.cameraTypes ?? v.camera_types)),
    equipmentTags: toLanguageArray(v.equipmentTags ?? v.equipment_tags),
    droneDetails: drone,
  };
}

/** Draft row may store `equipment` as JSON string; merge top-level `drone_details` column. */
function mergeEquipmentFromDraftRow(d) {
  const eq = normalizeEquipmentForState(d.equipment);
  let top = d.drone_details;
  if (typeof top === "string" && top.trim()) {
    try {
      top = JSON.parse(top);
    } catch {
      top = null;
    }
  }
  if (top && typeof top === "object" && !Array.isArray(top)) {
    return { ...eq, droneDetails: { ...eq.droneDetails, ...top } };
  }
  return eq;
}

/**
 * Same idea as equipment: `vehicles` may arrive as a JSON string; only spreading objects drops `types`.
 */
function normalizeVehiclesForState(raw) {
  const base = emptyForm().vehicles;
  if (raw == null || raw === "") return { ...base };
  let v = raw;
  if (typeof v === "string") {
    try {
      v = JSON.parse(v);
    } catch {
      return { ...base };
    }
  }
  if (typeof v !== "object" || Array.isArray(v)) return { ...base };
  const countRaw = v.count;
  const count =
    countRaw != null && countRaw !== "" && !Number.isNaN(Number(countRaw))
      ? countRaw
      : "";
  return {
    ...base,
    ...v,
    count,
    types: canonicalizeVehicleList(toLanguageArray(v.types ?? v.vehicle_types)),
    parkingRequired: Boolean(v.parkingRequired ?? v.parking_required ?? false),
    entryPermission: Boolean(v.entryPermission ?? v.entry_permission ?? false),
  };
}

/** Chakra NumberInput parses value with .toString() — null/undefined throws. */
function numberInputValue(v) {
  if (v == null || v === "") return "";
  return Number.isNaN(Number(v)) ? "" : v;
}

function normalizeCrewRow(el) {
  if (el == null) return { name: "", role: "" };
  if (typeof el !== "object" || Array.isArray(el)) return { name: "", role: "" };
  return {
    name: String(el.name ?? "").trim(),
    role: String(el.role ?? "").trim(),
  };
}

/** crew_details from API can be JSON string, object map, or malformed — always return [{ name, role }, ...]. */
function normalizeCrewDetails(raw) {
  if (raw == null || raw === "") return [{ name: "", role: "" }];
  let v = raw;
  if (typeof v === "string") {
    const s = v.trim();
    if (!s) return [{ name: "", role: "" }];
    try {
      v = JSON.parse(s);
    } catch {
      return [{ name: "", role: "" }];
    }
  }
  if (Array.isArray(v)) {
    return v.length ? v.map(normalizeCrewRow) : [{ name: "", role: "" }];
  }
  if (typeof v === "object") {
    const vals = Object.values(v);
    if (!vals.length) return [{ name: "", role: "" }];
    if (vals.every((x) => x != null && typeof x === "object" && !Array.isArray(x))) {
      return vals.map(normalizeCrewRow);
    }
    if ("name" in v || "role" in v) return [normalizeCrewRow(v)];
    return [{ name: "", role: "" }];
  }
  return [{ name: "", role: "" }];
}

/** API / MySQL may return documents as string or partial object — keep uploads after refresh. */
function normalizeDocuments(raw) {
  let v = raw;
  if (v == null || v === "") return { identity: {}, legal: {}, final: {} };
  if (typeof v === "string") {
    try {
      v = JSON.parse(v);
    } catch {
      return { identity: {}, legal: {}, final: {} };
    }
  }
  if (typeof v !== "object" || Array.isArray(v)) return { identity: {}, legal: {}, final: {} };
  const id = v.identity && typeof v.identity === "object" && !Array.isArray(v.identity) ? { ...v.identity } : {};
  const legal = v.legal && typeof v.legal === "object" && !Array.isArray(v.legal) ? { ...v.legal } : {};
  const fin = v.final && typeof v.final === "object" && !Array.isArray(v.final) ? { ...v.final } : {};
  return { identity: id, legal, final: fin };
}

function fileLabelFromPath(p) {
  if (!p || typeof p !== "string") return "";
  const s = p.replace(/\\/g, "/").split("/").pop() || p;
  return s.length > 36 ? `${s.slice(0, 16)}…${s.slice(-12)}` : s;
}

function buildDraftPayload(f, login) {
  const uid = login?.permanent_ID ? String(login.permanent_ID) : "";
  return {
    user_id: uid,
    project_title: f.project_title,
    project_type: f.project_type,
    production_house: f.production_house,
    producer_name: f.producer_name,
    director_name: f.director_name,
    line_producer: f.line_producer,
    language: toLanguageArray(f.language),
    description: f.description,
    applicant_type: f.applicant_type,
    contact_number: f.contact_number,
    email: f.email,
    phone_verified: true,
    email_verified: f.email_verified,
    location_name: f.location_name,
    address: f.address,
    city: f.city,
    state: f.state,
    landmark: f.landmark,
    location_lat: f.location_lat,
    location_lng: f.location_lng,
    location_type: f.location_type,
    indoor_outdoor: f.indoor_outdoor,
    area_usage: f.area_usage,
    permissions_required: toPermissionArray(f.permissions_required),
    near_airport_hint: f.near_airport_hint,
    start_datetime: f.start_datetime || null,
    end_datetime: f.end_datetime || null,
    total_days: f.total_days,
    daily_timing: f.daily_timing,
    night_shoot: f.night_shoot,
    crew_size: f.crew_size === "" ? null : Number(f.crew_size),
    crew_details: normalizeCrewDetails(f.crew_details),
    equipment: normalizeEquipmentForState(f.equipment),
    drone_details: normalizeEquipmentForState(f.equipment).droneDetails,
    lighting: f.lighting,
    generator_required: f.generator_required,
    vehicles: normalizeVehiclesForState(f.vehicles),
    special_requirements: f.special_requirements,
    utilities: f.utilities,
    documents: normalizeDocuments(f.documents),
    status: "draft",
  };
}

export default function LocationPermissionRequest() {
  const urlBase = getApiBaseUrl();
  const permBase = getPermissionApiBase();
  const toast = useToast();
  const navigate = useNavigate();
  const login = useMemo(() => JSON.parse(localStorage.getItem("login")) || "", []);

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [recordId, setRecordId] = useState(null);
  const [requestRef, setRequestRef] = useState("");
  const [saving, setSaving] = useState(false);
  const [emailOtp, setEmailOtp] = useState("");
  const [tagInput, setTagInput] = useState("");
  const recordIdRef = useRef(null);
  const saveDraftRef = useRef(async () => {});
  const formRef = useRef(form);
  const formCardRef = useRef(null);

  formRef.current = form;

  const footerBottom = useBreakpointValue({
    base: "calc(76px + env(safe-area-inset-bottom, 0px))",
    md: 0,
  });
  const footerZ = useBreakpointValue({ base: 1002, md: 1002 });
  const locRef = useRef(null);
  const autocompleteRef = useRef(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_KEY,
    libraries: LIBRARIES,
  });

  const setF = useCallback((patch) => setForm((p) => ({ ...p, ...patch })), []);
  const docs = normalizeDocuments(form.documents);

  const persistPayload = useCallback(() => buildDraftPayload(form, login), [form, login]);

  const saveDraft = useCallback(async (formPatch = null) => {
    const base = formRef.current;
    const merged = formPatch ? { ...base, ...formPatch } : base;
    setSaving(true);
    try {
      const body = buildDraftPayload(merged, login);
      const json = JSON.stringify(body);
      const id = recordIdRef.current;
      if (!id) {
        const { res, data: j } = await fetchApiJson(`${permBase}/create`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: json,
        });
        if (!res.ok || j.isError) throw new Error(j.message || "Save failed");
        recordIdRef.current = j.id;
        setRecordId(j.id);
        setRequestRef(j.request_ref || "");
        return j;
      }
      const { res, data: j } = await fetchApiJson(`${permBase}/update/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: json,
      });
      if (!res.ok || j.isError) throw new Error(j.message || "Save failed");
      return j;
    } catch (e) {
      const description = e && e.message ? e.message : String(e);
      toast({
        title: "Could not autosave",
        description,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      return null;
    } finally {
      setSaving(false);
    }
  }, [login, permBase, toast]);

  useEffect(() => {
    saveDraftRef.current = saveDraft;
  }, [saveDraft]);

  const debouncedSave = useDebouncedCallback(() => saveDraft(), 800);

  useEffect(() => {
    recordIdRef.current = recordId;
  }, [recordId]);

  useEffect(() => {
    if (login) {
      setForm((p) => ({
        ...p,
        email: p.email || login.email || "",
        contact_number: p.contact_number || login.mobile || "",
        producer_name: p.producer_name || login.name || "",
      }));
    }
  }, [login]);

  useEffect(() => {
    const uid = login?.permanent_ID;
    if (!uid) return;
    (async () => {
      try {
        const { res: r, data: j } = await fetchApiJson(
          `${permBase}/my/latest-draft?user_id=${encodeURIComponent(uid)}`
        );
        if (!r.ok) throw new Error("Draft load failed");
        if (j.data && j.data.id) {
          const d = j.data;
          setRecordId(d.id);
          setRequestRef(d.request_ref || "");
          setForm({
            ...emptyForm(),
            ...d,
            language: toLanguageArray(d.language),
            area_usage: d.area_usage || { mode: "full", partialDetail: "" },
            permissions_required: toPermissionArray(d.permissions_required),
            crew_details: normalizeCrewDetails(d.crew_details),
            crew_size:
              d.crew_size != null && d.crew_size !== "" && !Number.isNaN(Number(d.crew_size))
                ? d.crew_size
                : "",
            equipment: mergeEquipmentFromDraftRow(d),
            vehicles: normalizeVehiclesForState(d.vehicles),
            special_requirements: d.special_requirements || emptyForm().special_requirements,
            utilities: d.utilities || emptyForm().utilities,
            documents: normalizeDocuments(d.documents),
            start_datetime: d.start_datetime ? d.start_datetime.slice(0, 16) : "",
            end_datetime: d.end_datetime ? d.end_datetime.slice(0, 16) : "",
          });
        }
      } catch (_) {}
    })();
  }, [login, permBase]);

  useEffect(() => {
    const hasAnyUpload = ["identity", "legal", "final"].some((sec) =>
      Object.values(form.documents?.[sec] || {}).some((x) => Boolean(x && String(x).trim()))
    );
    const hasContent =
      recordId != null ||
      hasAnyUpload ||
      Boolean(form.project_title?.trim()) ||
      Boolean(form.email?.trim()) ||
      Boolean(form.contact_number?.trim()) ||
      Boolean(form.description?.trim()) ||
      step > 0;
    if (!hasContent) return;
    debouncedSave();
  }, [form, step, recordId, debouncedSave]);

  const onPlaceChanged = useCallback(() => {
    const ac = autocompleteRef.current;
    if (!ac) return;
    const place = ac.getPlace();
    if (!place?.geometry) return;
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    let city = "";
    let state = "";
    (place.address_components || []).forEach((c) => {
      if (c.types.includes("locality")) city = c.long_name;
      if (c.types.includes("administrative_area_level_1")) state = c.long_name;
    });
    const types = place.types || [];
    const addr = place.formatted_address || "";
    const nearAir =
      types.includes("airport") ||
      /airport/i.test(addr) ||
      types.includes("point_of_interest");
    setForm((p) => {
      let perms = [...toPermissionArray(p.permissions_required)];
      if (nearAir && !perms.includes("airport_authority")) {
        perms.push("airport_authority");
        toast({ title: "Airport proximity", description: "Airport Authority added — review in Permissions step.", status: "info", duration: 4000 });
      }
      return {
        ...p,
        location_name: place.name || p.location_name,
        address: addr,
        city: city || p.city,
        state: state || p.state,
        location_lat: String(lat),
        location_lng: String(lng),
        near_airport_hint: nearAir,
        permissions_required: perms,
      };
    });
  }, [toast]);

  const upload = async (file, slot) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    let res;
    let j;
    try {
      ({ res, data: j } = await fetchApiJson(`${permBase}/upload`, { method: "POST", body: fd }));
    } catch (e) {
      toast({ title: "Upload failed", description: e.message, status: "error" });
      return;
    }
    if (!res.ok || j.isError) {
      toast({ title: "Upload failed", status: "error" });
      return;
    }
    const [section, key] = slot.split(".");
    setForm((p) => {
      const d = normalizeDocuments(p.documents);
      const nextDocs = { ...d, [section]: { ...d[section], [key]: j.path } };
      queueMicrotask(() => saveDraftRef.current({ documents: nextDocs }));
      return { ...p, documents: nextDocs };
    });
    toast({ title: "Saved on server", description: "You can refresh — this file stays attached.", status: "success" });
  };

  const clearUpload = (slot) => {
    const [section, key] = slot.split(".");
    setForm((p) => {
      const d = normalizeDocuments(p.documents);
      const nextSec = { ...d[section] };
      delete nextSec[key];
      const nextDocs = { ...d, [section]: nextSec };
      queueMicrotask(() => saveDraftRef.current({ documents: nextDocs }));
      return { ...p, documents: nextDocs };
    });
    toast({ title: "File removed", status: "info" });
  };

  const validateStep = (s) => {
    switch (s) {
      case 0:
        if (!form.project_title?.trim()) return "Project title required";
        if (!form.project_type) return "Project type required";
        if (!form.language?.length) return "Select at least one language";
        return "";
      case 1: {
        const ph = String(form.contact_number || "").replace(/\D/g, "");
        if (ph.length < 10) return "Valid contact number required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email || "")) return "Valid email required";
        if (!form.email_verified) return "Verify email with OTP";
        const idn = docs.identity || {};
        const need = ["aadhaar", "pan", "producer_card", "authority_letter", "company_letterhead", "address_proof"];
        for (const k of need) {
          if (!idn[k]) return `Upload ${k.replace(/_/g, " ")}`;
        }
        if (form.applicant_type === "company" && !idn.gst_certificate) return "GST certificate required for company";
        return "";
      }
      case 2:
        if (!form.location_name?.trim()) return "Location name required";
        if (!form.address?.trim()) return "Address required";
        if (!form.location_type) return "Location type required";
        if (form.area_usage?.mode === "partial" && !form.area_usage?.partialDetail?.trim())
          return "Describe partial area";
        return "";
      case 3:
        if (!toPermissionArray(form.permissions_required).length) return "Select at least one permission";
        return "";
      case 4:
        if (!form.start_datetime || !form.end_datetime) return "Start and end required";
        if (new Date(form.end_datetime) <= new Date(form.start_datetime)) return "End must be after start";
        if (!form.daily_timing) return "Daily timing required";
        return "";
      case 5:
        if (!form.crew_size || Number(form.crew_size) < 1) return "Crew size required";
        if (!normalizeCrewDetails(form.crew_details).some((c) => c.name && c.role))
          return "Add at least one key crew member";
        return "";
      case 6:
        if (!normalizeEquipmentForState(form.equipment).cameraTypes.length) return "Select camera type(s)";
        return "";
      case 7:
        {
          const vh = normalizeVehiclesForState(form.vehicles);
          if (!vh.count || Number(vh.count) < 0) return "Vehicle count required";
        }
        return "";
      case 10: {
        const lg = docs.legal || {};
        if (!lg.indemnity_bond || !lg.insurance_certificate) return "Indemnity & insurance required";
        return "";
      }
      default:
        return "";
    }
  };

  const goNext = async () => {
    try {
      const err = validateStep(step);
      if (err) {
        toast({ title: err, status: "warning" });
        return;
      }
      const saved = await saveDraft();
      if (saved === null) {
        return;
      }
      setStep((x) => Math.min(x + 1, STEP_LABELS.length - 1));
      queueMicrotask(() => {
        formCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    } catch (e) {
      toast({
        title: "Could not go to next step",
        description: e && e.message ? e.message : String(e),
        status: "error",
        duration: 8000,
        isClosable: true,
      });
    }
  };

  const goBack = () => {
    setStep((x) => Math.max(x - 1, 0));
    queueMicrotask(() => {
      formCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const togglePermissionValue = useCallback((key) => {
    setForm((p) => {
      const arr = [...toPermissionArray(p.permissions_required)];
      if (arr.includes(key)) {
        return { ...p, permissions_required: arr.filter((x) => x !== key) };
      }
      if (key === "asi") {
        return {
          ...p,
          heritage_site: true,
          permissions_required: [...arr, "asi"],
        };
      }
      return { ...p, permissions_required: [...arr, key] };
    });
  }, []);

  useEffect(() => {
    if (!form.start_datetime || !form.end_datetime) return;
    const a = new Date(form.start_datetime);
    const b = new Date(form.end_datetime);
    if (b > a) {
      const days = Math.max(1, Math.ceil((b - a) / (86400000)));
      setForm((p) => (p.total_days !== days ? { ...p, total_days: days } : p));
    }
  }, [form.start_datetime, form.end_datetime]);

  useEffect(() => {
    setForm((p) => {
      const prev = toPermissionArray(p.permissions_required);
      let perms = [...prev];
      if (p.heritage_site) {
        if (!perms.includes("asi")) perms.push("asi");
      } else {
        perms = perms.filter((x) => x !== "asi");
      }
      if (perms.join(",") === prev.join(",")) return p;
      return { ...p, permissions_required: perms };
    });
  }, [form.heritage_site]);

  const sendEmailOtp = async () => {
    if (!form.email?.trim()) {
      toast({ title: "Enter email first", status: "warning" });
      return;
    }
    const res = await fetch(`${urlBase}/leads/send-booking-otp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: form.email.trim() }),
    });
    const j = await res.json();
    if (!res.ok || !j.success) {
      toast({ title: j.message || "Could not send OTP", status: "error" });
      return;
    }
    toast({ title: "OTP sent to email", status: "success" });
  };

  const verifyEmailOtp = async () => {
    const res = await fetch(`${urlBase}/leads/verify-booking-otp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: form.email.trim(), otp: emailOtp }),
    });
    const j = await res.json();
    if (!res.ok || !j.success) {
      toast({ title: j.message || "Invalid OTP", status: "error" });
      return;
    }
    setF({ email_verified: true });
    toast({ title: "Email verified", status: "success" });
  };

  const submitFinal = async () => {
    for (let s = 0; s <= 10; s++) {
      const e = validateStep(s);
      if (e) {
        toast({ title: "Complete earlier steps", description: e, status: "warning" });
        setStep(s);
        return;
      }
    }
    const legalDocs = docs.legal || {};
    if (!legalDocs.undertaking_form) {
      toast({ title: "Upload undertaking form", status: "warning" });
      setStep(10);
      return;
    }
    await saveDraft();
    const submitId = recordIdRef.current;
    if (!submitId) {
      toast({ title: "Could not save application", status: "error" });
      return;
    }
    try {
      const { res, data: j } = await fetchApiJson(`${permBase}/update/${submitId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...persistPayload(), status: "submitted" }),
      });
      if (!res.ok || j.isError) {
        toast({ title: j.message || "Submission blocked", status: "error", duration: 6000 });
        return;
      }
      navigate("/location-permission/success", { state: { ref: j.data?.request_ref || requestRef, id: submitId } });
    } catch (e) {
      toast({ title: "Submit failed", description: e.message, status: "error", duration: 8000 });
    }
  };

  const renderUploadRow = (label, slot, accept = ".pdf,.jpg,.jpeg,.png,.webp", helperText) => {
    const [sec, k] = slot.split(".");
    const stored = docs[sec]?.[k];
    const inputId = `perm-up-${slot.replace(/\./g, "-")}`;
    const fileUrl = stored ? apiAssetUrl(urlBase, stored) : "";
    const onDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const f = e.dataTransfer?.files?.[0];
      if (f) upload(f, slot);
    };
    return (
      <FormControl key={slot}>
        <FormLabel fontSize="sm" fontWeight="semibold" color="gray.800" mb={2} letterSpacing="-0.01em">
          {label}
        </FormLabel>
        <Box
          role="group"
          borderRadius="xl"
          borderWidth="1px"
          borderStyle="dashed"
          borderColor={stored ? "emerald.300" : "gray.200"}
          bg={stored ? "emerald.50" : "gray.50"}
          px={4}
          py={3}
          transition="border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease"
          _hover={{
            borderColor: stored ? "emerald.400" : "indigo.300",
            boxShadow: "0 4px 20px rgba(99, 102, 241, 0.08)",
            bg: stored ? "emerald.50" : "white",
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={onDrop}
        >
          {stored ? (
            <Stack spacing={3}>
              <HStack spacing={3} align="flex-start">
                <Flex
                  align="center"
                  justify="center"
                  w={10}
                  h={10}
                  borderRadius="lg"
                  bg="emerald.100"
                  color="emerald.800"
                  flexShrink={0}
                >
                  <Icon as={FiFile} boxSize={5} />
                </Flex>
                <Box flex={1} minW={0}>
                  <HStack spacing={2} flexWrap="wrap">
                    <Badge colorScheme="emerald" display="inline-flex" alignItems="center" gap={1}>
                      <FiCheck /> On file
                    </Badge>
                  </HStack>
                  <Text fontSize="sm" fontWeight="medium" color="gray.800" mt={1} noOfLines={2} title={fileUrl}>
                    {fileLabelFromPath(stored)}
                  </Text>
                  {helperText ? (
                    <Text fontSize="xs" color="gray.500" mt={1}>{helperText}</Text>
                  ) : null}
                </Box>
              </HStack>
              <HStack spacing={2} flexWrap="wrap">
                {fileUrl ? (
                  <Link href={fileUrl} isExternal fontSize="sm" color="indigo.600" fontWeight="medium">
                    <Icon as={FiExternalLink} mr={1} mb="-2px" /> View
                  </Link>
                ) : null}
                <Button
                  as="label"
                  htmlFor={inputId}
                  size="xs"
                  variant="outline"
                  leftIcon={<FiRefreshCw />}
                  cursor="pointer"
                  borderColor="gray.500"
                  color="gray.800"
                  bg="white"
                  fontWeight="semibold"
                  _hover={{ bg: "gray.100", color: "gray.900" }}
                >
                  Replace
                  <Input
                    id={inputId}
                    type="file"
                    hidden
                    accept={accept}
                    onChange={(e) => {
                      upload(e.target.files?.[0], slot);
                      e.target.value = "";
                    }}
                  />
                </Button>
                <Button size="xs" variant="ghost" colorScheme="red" leftIcon={<FiTrash2 />} onClick={() => clearUpload(slot)}>
                  Remove
                </Button>
              </HStack>
            </Stack>
          ) : (
            <Stack spacing={2} align="center" py={1}>
              <Icon as={FiUpload} boxSize={8} color="gray.400" />
              <Text fontSize="sm" color="gray.600" textAlign="center">
                Drag & drop or tap to upload
              </Text>
              <Button as="label" htmlFor={inputId} {...uploadPrimaryButtonProps}>
                Choose file
                <Input
                  id={inputId}
                  type="file"
                  hidden
                  accept={accept}
                  onChange={(e) => {
                    upload(e.target.files?.[0], slot);
                    e.target.value = "";
                  }}
                />
              </Button>
              {helperText ? <Text fontSize="xs" color="gray.500">{helperText}</Text> : null}
            </Stack>
          )}
        </Box>
      </FormControl>
    );
  };

  const stepBody = () => {
    switch (step) {
      case 0:
        return (
          <Stack spacing={10}>
            <FormSection
              title="Production"
              hint="Use the title and type you plan to show on permission letters."
            >
              <Stack spacing={5}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                  <FormControl isRequired>
                    <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                      Project title
                    </FormLabel>
                    <Input
                      {...inputBaseProps}
                      placeholder="e.g. Working title — Monsoon Unit"
                      value={form.project_title}
                      onChange={(e) => setF({ project_title: e.target.value })}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                      Project type
                    </FormLabel>
                    <Select
                      {...selectBaseProps}
                      placeholder="Choose type"
                      value={form.project_type}
                      onChange={(e) => setF({ project_type: e.target.value })}
                    >
                      {PROJECT_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </SimpleGrid>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                  <FormControl>
                    <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                      Production house
                    </FormLabel>
                    <Input
                      {...inputBaseProps}
                      placeholder="Banner or company name"
                      value={form.production_house}
                      onChange={(e) => setF({ production_house: e.target.value })}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                      Producer
                    </FormLabel>
                    <Input
                      {...inputBaseProps}
                      placeholder="Producer name"
                      value={form.producer_name}
                      onChange={(e) => setF({ producer_name: e.target.value })}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                      Director
                    </FormLabel>
                    <Input
                      {...inputBaseProps}
                      placeholder="Director name"
                      value={form.director_name}
                      onChange={(e) => setF({ director_name: e.target.value })}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                      Line producer
                    </FormLabel>
                    <Input
                      {...inputBaseProps}
                      placeholder="Optional"
                      value={form.line_producer}
                      onChange={(e) => setF({ line_producer: e.target.value })}
                    />
                  </FormControl>
                </SimpleGrid>
              </Stack>
            </FormSection>

            <FormSection title="Languages" hint="Select all dialogue and narration languages — you can pick several.">
              <Box
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="xl"
                bg="white"
                p={{ base: 4, md: 5 }}
                position="relative"
                zIndex={1}
              >
                <Wrap spacing={3}>
                  {(() => {
                    const selected = new Set(toLanguageArray(form.language));
                    return LANGS.map((l) => (
                      <WrapItem key={l}>
                        <Checkbox
                          id={`location-perm-lang-${l}`}
                          isChecked={selected.has(l)}
                          onChange={(e) => {
                            const on = e.target.checked;
                            setForm((p) => {
                              const cur = toLanguageArray(p.language);
                              const next = on
                                ? cur.includes(l)
                                  ? cur
                                  : [...cur, l]
                                : cur.filter((x) => x !== l);
                              return { ...p, language: next };
                            });
                          }}
                          {...checkboxVisibleProps}
                        >
                          {l}
                        </Checkbox>
                      </WrapItem>
                    ));
                  })()}
                </Wrap>
              </Box>
            </FormSection>

            <FormSection title="Description" hint="Optional one-liner or short brief for authorities.">
              <FormControl>
                <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                  Synopsis / notes
                </FormLabel>
                <Textarea
                  {...textareaBaseProps}
                  rows={4}
                  value={form.description}
                  onChange={(e) => setF({ description: e.target.value })}
                  placeholder="Short brief — optional"
                />
              </FormControl>
            </FormSection>
          </Stack>
        );
      case 1:
        return (
          <Stack spacing={8}>
            <FormSection title="Applicant type" hint="Choose how you are applying — company needs GST as well.">
              <HStack p={1} bg="gray.100" borderRadius="xl" w="fit-content" flexWrap="wrap">
                <Button
                  size="md"
                  borderRadius="lg"
                  px={6}
                  fontWeight="semibold"
                  onClick={() => setF({ applicant_type: "individual" })}
                  {...(form.applicant_type === "individual"
                    ? primaryButtonProps
                    : {
                        variant: "ghost",
                        bg: "transparent",
                        color: "gray.800",
                        _hover: { bg: "white", color: "gray.900" },
                      })}
                >
                  Individual
                </Button>
                <Button
                  size="md"
                  borderRadius="lg"
                  px={6}
                  fontWeight="semibold"
                  onClick={() => setF({ applicant_type: "company" })}
                  {...(form.applicant_type === "company"
                    ? primaryButtonProps
                    : {
                        variant: "ghost",
                        bg: "transparent",
                        color: "gray.800",
                        _hover: { bg: "white", color: "gray.900" },
                      })}
                >
                  Company
                </Button>
              </HStack>
            </FormSection>

            <FormSection title="Contact & verification" hint="We send a one-time code to your email. Your number should match production contact details.">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                <FormControl isRequired>
                  <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                    Email
                  </FormLabel>
                  <Input
                    type="email"
                    {...inputBaseProps}
                    placeholder="producer@banner.com"
                    value={form.email}
                    onChange={(e) => setF({ email: e.target.value, email_verified: false })}
                  />
                  <Box
                    mt={3}
                    p={4}
                    bg="white"
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor="gray.100"
                  >
                    <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2} textTransform="uppercase" letterSpacing="0.06em">
                      Email OTP
                    </Text>
                    <Stack direction={{ base: "column", sm: "row" }} spacing={2} align={{ sm: "center" }} flexWrap="wrap">
                      <Button
                        size="sm"
                        borderRadius="lg"
                        fontWeight="semibold"
                        borderWidth="2px"
                        borderColor={PRIMARY_BG}
                        color={PRIMARY_BG}
                        bg="white"
                        _hover={{ bg: "indigo.50", color: PRIMARY_BG_HOVER }}
                        onClick={sendEmailOtp}
                      >
                        Send code
                      </Button>
                      <Input
                        placeholder="6-digit code"
                        size="md"
                        maxW={{ base: "100%", sm: "140px" }}
                        borderRadius="lg"
                        borderWidth="2px"
                        borderColor="gray.300"
                        value={emailOtp}
                        onChange={(e) => setEmailOtp(e.target.value)}
                      />
                      <Button
                        size="sm"
                        borderRadius="lg"
                        fontWeight="semibold"
                        {...primaryButtonProps}
                        onClick={verifyEmailOtp}
                      >
                        Verify email
                      </Button>
                      {form.email_verified ? (
                        <Badge colorScheme="emerald" borderRadius="full" px={3} py={1} display="inline-flex" alignItems="center" gap={1}>
                          <Icon as={FiCheck} /> Verified
                        </Badge>
                      ) : null}
                    </Stack>
                  </Box>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                    Contact number
                  </FormLabel>
                  <Input
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    {...inputBaseProps}
                    placeholder="10-digit mobile"
                    value={form.contact_number}
                    onChange={(e) => setF({ contact_number: e.target.value })}
                  />
                  <FormHelperText mt={2}>Used by authorities for quick follow-up.</FormHelperText>
                </FormControl>
              </SimpleGrid>
            </FormSection>

            <Divider borderColor="gray.200" />
            <Box
              borderRadius="2xl"
              borderWidth="1px"
              borderColor="indigo.100"
              bg="linear-gradient(145deg, #f8fafc 0%, #eef2ff 40%, #f5f3ff 100%)"
              p={{ base: 4, md: 7 }}
              boxShadow="0 8px 40px rgba(79, 70, 229, 0.07)"
            >
              <HStack justify="space-between" align="flex-start" mb={5} flexWrap="wrap" gap={3}>
                <Box>
                  <Heading size="sm" color="gray.800" letterSpacing="-0.02em">
                    Identity & company proofs
                  </Heading>
                  <Text fontSize="sm" color="gray.600" mt={2} maxW="lg" lineHeight="tall">
                    Files attach to your draft automatically. Refresh anytime — use Replace or Remove to change a document.
                  </Text>
                </Box>
                <Badge colorScheme="indigo" variant="subtle" px={3} py={1.5} borderRadius="full" fontSize="xs">
                  Auto-saved
                </Badge>
              </HStack>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {renderUploadRow("Aadhaar", "identity.aadhaar", ".pdf,.jpg,.jpeg,.png,.webp", "PDF or clear image")}
                {renderUploadRow("PAN", "identity.pan", ".pdf,.jpg,.jpeg,.png,.webp")}
                {renderUploadRow("Producer card / Film ID", "identity.producer_card")}
                {renderUploadRow("Authority letter", "identity.authority_letter")}
                {renderUploadRow("Company letterhead (PDF)", "identity.company_letterhead", ".pdf", "PDF only")}
                {form.applicant_type === "company"
                  ? renderUploadRow("GST certificate", "identity.gst_certificate", ".pdf,.jpg,.jpeg,.png,.webp", "Required for company")
                  : null}
                {renderUploadRow("Address proof", "identity.address_proof")}
              </SimpleGrid>
            </Box>
          </Stack>
        );
      case 2:
        return (
          <Stack spacing={8}>
            <FormSection
              title="Search location"
              hint="Pick a place so we can pre-fill address and map coordinates."
            >
              {!isLoaded ? (
                <Center py={10} flexDirection="column" gap={3}>
                  <Spinner color="indigo.500" thickness="3px" size="lg" />
                  <Text fontSize="sm" color="gray.600">
                    Loading maps…
                  </Text>
                </Center>
              ) : (
                <Autocomplete
                  onLoad={(ac) => {
                    autocompleteRef.current = ac;
                  }}
                  onPlaceChanged={onPlaceChanged}
                >
                  <FormControl>
                    <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                      <HStack spacing={2} align="center" as="span">
                        <Icon as={FiMapPin} color={PRIMARY_BG} flexShrink={0} />
                        <Text as="span">Location name (Google Places)</Text>
                        <Text as="span" color="red.500" fontWeight="bold">
                          *
                        </Text>
                      </HStack>
                    </FormLabel>
                    <Input
                      ref={locRef}
                      {...inputBaseProps}
                      placeholder="Search landmark, studio, or address"
                    />
                  </FormControl>
                </Autocomplete>
              )}
            </FormSection>

            <FormSection title="Address" hint="You can edit any line after picking from the map.">
              <Stack spacing={5}>
                <FormControl isRequired>
                  <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                    Full address
                  </FormLabel>
                  <Textarea
                    {...textareaBaseProps}
                    minH="110px"
                    value={form.address}
                    onChange={(e) => setF({ address: e.target.value })}
                    rows={3}
                  />
                </FormControl>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                  <FormControl>
                    <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                      City
                    </FormLabel>
                    <Input
                      {...inputBaseProps}
                      value={form.city}
                      onChange={(e) => setF({ city: e.target.value })}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                      State
                    </FormLabel>
                    <Input
                      {...inputBaseProps}
                      value={form.state}
                      onChange={(e) => setF({ state: e.target.value })}
                    />
                  </FormControl>
                </SimpleGrid>
                <FormControl>
                  <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                    Landmark
                  </FormLabel>
                  <Input
                    {...inputBaseProps}
                    placeholder="e.g. Near City Mall"
                    value={form.landmark}
                    onChange={(e) => setF({ landmark: e.target.value })}
                  />
                </FormControl>
              </Stack>
            </FormSection>

            <FormSection title="Site classification" hint="Accurate classification helps route your file to the right department.">
              <Stack spacing={5}>
                <FormControl isRequired>
                  <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                    Type of location
                  </FormLabel>
                  <Select
                    {...selectBaseProps}
                    placeholder="Select category"
                    value={form.location_type}
                    onChange={(e) => setF({ location_type: e.target.value })}
                  >
                    {LOCATION_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <Flex
                    align="center"
                    justify="space-between"
                    wrap="wrap"
                    gap={4}
                    p={4}
                    borderRadius="xl"
                    bg="gray.50"
                    borderWidth="1px"
                    borderColor="gray.100"
                  >
                    <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={0}>
                      Indoor / outdoor
                    </FormLabel>
                    <HStack spacing={3}>
                      <Text fontSize="sm" color={form.indoor_outdoor === "outdoor" ? "indigo.700" : "gray.500"} fontWeight="medium">
                        Outdoor
                      </Text>
                      <Switch
                        {...switchVisibleProps}
                        size="lg"
                        isChecked={form.indoor_outdoor === "indoor"}
                        onChange={(e) => setF({ indoor_outdoor: e.target.checked ? "indoor" : "outdoor" })}
                      />
                      <Text fontSize="sm" color={form.indoor_outdoor === "indoor" ? "indigo.700" : "gray.500"} fontWeight="medium">
                        Indoor
                      </Text>
                    </HStack>
                  </Flex>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                    Area usage
                  </FormLabel>
                  <Select
                    {...selectBaseProps}
                    value={form.area_usage?.mode || "full"}
                    onChange={(e) => setF({ area_usage: { ...form.area_usage, mode: e.target.value } })}
                  >
                    <option value="full">Full area</option>
                    <option value="partial">Partial area</option>
                  </Select>
                </FormControl>
                {form.area_usage?.mode === "partial" ? (
                  <FormControl isRequired>
                    <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                      Partial area details
                    </FormLabel>
                    <Input
                      {...inputBaseProps}
                      placeholder="Describe which floors / zones"
                      value={form.area_usage?.partialDetail || ""}
                      onChange={(e) => setF({ area_usage: { ...form.area_usage, partialDetail: e.target.value } })}
                    />
                  </FormControl>
                ) : null}
              </Stack>
            </FormSection>
          </Stack>
        );
      case 3:
        return (
          <Stack spacing={6}>
            <Box
              p={5}
              borderRadius="xl"
              borderWidth="1px"
              borderColor="amber.100"
              bg="linear-gradient(135deg, #fffbeb 0%, #fff7ed 100%)"
            >
              <Checkbox
                isChecked={form.heritage_site}
                onChange={(e) => setF({ heritage_site: e.target.checked })}
                sx={{
                  ".chakra-checkbox__control": {
                    borderWidth: "2px",
                    borderColor: "gray.600",
                    _checked: {
                      bg: "orange.500 !important",
                      borderColor: "orange.600 !important",
                      color: "white !important",
                    },
                  },
                  ".chakra-checkbox__label": { fontWeight: "medium", color: "gray.800 !important" },
                }}
              >
                Shooting at a heritage / ASI site
              </Checkbox>
              <Text fontSize="xs" color="gray.600" mt={2} ml={7}>
                Enables ASI permission in the list below when checked.
              </Text>
            </Box>
            <Box borderWidth="1px" borderColor="gray.200" borderRadius="xl" bg="white" p={{ base: 4, md: 5 }}>
              <Text fontSize="sm" fontWeight="semibold" color="gray.800" mb={4}>
                Permits to apply for
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                {(() => {
                  const perms = new Set(toPermissionArray(form.permissions_required));
                  return PERMISSION_OPTS.map((o) => (
                    <Box
                      key={o.v}
                      bg="gray.50"
                      borderRadius="lg"
                      borderWidth="1px"
                      borderColor="gray.200"
                      px={4}
                      py={3}
                      transition="box-shadow 0.15s ease"
                      _hover={{ boxShadow: "md", borderColor: "gray.300" }}
                    >
                      <Checkbox
                        isChecked={perms.has(o.v)}
                        onChange={() => togglePermissionValue(o.v)}
                        {...checkboxVisibleProps}
                      >
                        {o.label}
                      </Checkbox>
                      {o.v === "asi" && !form.heritage_site ? (
                        <Text fontSize="xs" color="gray.600" mt={2} ml={7}>
                          Selecting ASI also turns on “heritage site” at the top.
                        </Text>
                      ) : null}
                    </Box>
                  ));
                })()}
              </SimpleGrid>
            </Box>
            {form.near_airport_hint ? (
              <Flex align="flex-start" gap={3} bg="indigo.50" borderWidth="1px" borderColor="indigo.100" p={4} borderRadius="xl">
                <Icon as={FiMapPin} color="indigo.600" boxSize={5} mt={0.5} flexShrink={0} />
                <Text fontSize="sm" color="indigo.900" lineHeight="tall">
                  This pin may be near airport infrastructure — Airport Authority is suggested in your list. Adjust if it does not apply.
                </Text>
              </Flex>
            ) : null}
          </Stack>
        );
      case 4:
        return (
          <Stack spacing={6}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
              <FormControl isRequired>
                <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                  Start
                </FormLabel>
                <Input
                  type="datetime-local"
                  {...inputBaseProps}
                  value={form.start_datetime}
                  onChange={(e) => setF({ start_datetime: e.target.value })}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                  End
                </FormLabel>
                <Input
                  type="datetime-local"
                  {...inputBaseProps}
                  value={form.end_datetime}
                  onChange={(e) => setF({ end_datetime: e.target.value })}
                />
              </FormControl>
            </SimpleGrid>
            <Flex
              align="center"
              justify="space-between"
              flexWrap="wrap"
              gap={3}
              p={5}
              borderRadius="xl"
              bg="indigo.50"
              borderWidth="1px"
              borderColor="indigo.100"
            >
              <Box>
                <Text fontSize="xs" fontWeight="semibold" color="indigo.600" textTransform="uppercase" letterSpacing="0.06em">
                  Calendar span
                </Text>
                <Text fontSize="sm" color="gray.700" mt={1}>
                  Estimated shoot days from your dates
                </Text>
              </Box>
              <Badge colorScheme="indigo" fontSize="md" px={4} py={2} borderRadius="full">
                {form.total_days
                  ? `${form.total_days} day${Number(form.total_days) !== 1 ? "s" : ""}`
                  : "—"}
              </Badge>
            </Flex>
            <FormControl isRequired>
              <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                Daily call time
              </FormLabel>
              <Input
                type="time"
                {...inputBaseProps}
                value={form.daily_timing}
                onChange={(e) => setF({ daily_timing: e.target.value })}
              />
              <FormHelperText>Typical start / reporting time each shooting day.</FormHelperText>
            </FormControl>
            <FormControl>
              <Flex
                align="center"
                justify="space-between"
                wrap="wrap"
                gap={4}
                p={4}
                borderRadius="xl"
                borderWidth="1px"
                borderColor="gray.100"
                bg="white"
              >
                <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={0}>
                  Night shoot planned
                </FormLabel>
                <Switch
                  {...switchVisibleProps}
                  size="lg"
                  isChecked={form.night_shoot}
                  onChange={(e) => setF({ night_shoot: e.target.checked })}
                />
              </Flex>
            </FormControl>
          </Stack>
        );
      case 5: {
        const crewRows = normalizeCrewDetails(form.crew_details);
        return (
          <Stack spacing={8}>
            <FormControl isRequired>
              <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                Total crew size
              </FormLabel>
              <NumberInput
                value={numberInputValue(form.crew_size)}
                onChange={(_, v) => setF({ crew_size: v })}
                min={1}
                size="lg"
              >
                <NumberInputField {...numberFieldProps} placeholder="e.g. 45" />
              </NumberInput>
            </FormControl>
            <FormSection title="Key crew" hint="List main heads — at least one name with role is required.">
              <Stack spacing={4}>
                {crewRows.map((c, i) => (
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} key={i}>
                    <Input
                      {...inputBaseProps}
                      placeholder="Full name"
                      value={c.name}
                      onChange={(e) => {
                        const next = [...crewRows];
                        next[i] = { ...next[i], name: e.target.value };
                        setF({ crew_details: next });
                      }}
                    />
                    <Select
                      {...selectBaseProps}
                      placeholder="Role"
                      value={c.role}
                      onChange={(e) => {
                        const next = [...crewRows];
                        next[i] = { ...next[i], role: e.target.value };
                        setF({ crew_details: next });
                      }}
                    >
                      <option value="">Select role</option>
                      {CREW_ROLES.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </Select>
                  </SimpleGrid>
                ))}
                <Button
                  size="md"
                  variant="outline"
                  colorScheme="indigo"
                  borderRadius="xl"
                  borderStyle="dashed"
                  onClick={() => setF({ crew_details: [...crewRows, { name: "", role: "" }] })}
                >
                  + Add crew member
                </Button>
              </Stack>
            </FormSection>
          </Stack>
        );
      }
      case 6:
        return (
          <Stack spacing={8}>
            <FormControl isRequired>
              <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={3}>
                Camera type
              </FormLabel>
              <Text fontSize="xs" color="gray.500" mb={2}>
                Tap options to select or clear — you can choose more than one.
              </Text>
              <Wrap spacing={3}>
                {CAMERA_OPTS.map((c) => {
                  const selected = normalizeEquipmentForState(form.equipment).cameraTypes.includes(c);
                  return (
                    <WrapItem key={c}>
                      <Button
                        type="button"
                        size="md"
                        px={5}
                        py={6}
                        h="auto"
                        borderRadius="lg"
                        borderWidth="2px"
                        fontWeight="semibold"
                        variant="outline"
                        borderColor={selected ? "gray.700" : "gray.300"}
                        bg={selected ? "gray.700" : "white"}
                        color={selected ? "gray.100" : "gray.800"}
                        _hover={{
                          bg: selected ? "gray.600" : "gray.50",
                          borderColor: "gray.500",
                        }}
                        onClick={() => {
                          setForm((p) => {
                            const eq0 = normalizeEquipmentForState(p.equipment);
                            const cur = eq0.cameraTypes;
                            const next = cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c];
                            const hasDroneCam = next.includes("Drone");
                            let perms = [...toPermissionArray(p.permissions_required)];
                            if (hasDroneCam && !perms.includes("drone")) perms.push("drone");
                            else if (!hasDroneCam) perms = perms.filter((x) => x !== "drone");
                            return {
                              ...p,
                              equipment: { ...eq0, cameraTypes: next },
                              permissions_required: perms,
                            };
                          });
                        }}
                      >
                        {c}
                      </Button>
                    </WrapItem>
                  );
                })}
              </Wrap>
            </FormControl>
            <FormControl>
              <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                Equipment tags
              </FormLabel>
              <Input
                {...inputBaseProps}
                placeholder="e.g. Crane — press Enter to add"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const t = tagInput.trim();
                    if (!t) return;
                    setForm((p) => {
                      const eq = normalizeEquipmentForState(p.equipment);
                      const tags = [...eq.equipmentTags, t];
                      return { ...p, equipment: { ...eq, equipmentTags: tags } };
                    });
                    setTagInput("");
                  }
                }}
              />
              <Wrap mt={3} spacing={2}>
                {normalizeEquipmentForState(form.equipment).equipmentTags.map((t) => (
                  <WrapItem key={t}>
                    <Badge
                      cursor="pointer"
                      borderRadius="full"
                      px={3}
                      py={1}
                      colorScheme="indigo"
                      variant="subtle"
                      onClick={() => {
                        setForm((p) => {
                          const eq = normalizeEquipmentForState(p.equipment);
                          const tags = eq.equipmentTags.filter((x) => x !== t);
                          return { ...p, equipment: { ...eq, equipmentTags: tags } };
                        });
                      }}
                    >
                      {t} ×
                    </Badge>
                  </WrapItem>
                ))}
              </Wrap>
            </FormControl>
            {(toPermissionArray(form.permissions_required).includes("drone") ||
              normalizeEquipmentForState(form.equipment).cameraTypes.includes("Drone")) ? (
              <Card variant="outline" borderColor="indigo.200" borderRadius="xl" overflow="hidden" boxShadow="sm">
                <Box bg="indigo.50" px={5} py={3} borderBottomWidth="1px" borderColor="indigo.100">
                  <Heading size="sm" color="indigo.900">
                    Drone details
                  </Heading>
                  <Text fontSize="xs" color="indigo.700" mt={1}>
                    DGCA / operator identification helps complete drone permission.
                  </Text>
                </Box>
                <CardBody pt={5}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                    <FormControl>
                      <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                        UA registration / ID
                      </FormLabel>
                      <Input
                        {...inputBaseProps}
                        value={
                          normalizeEquipmentForState(form.equipment).droneDetails.registrationId || ""
                        }
                        onChange={(e) =>
                          setForm((p) => {
                            const eq = normalizeEquipmentForState(p.equipment);
                            return {
                              ...p,
                              equipment: {
                                ...eq,
                                droneDetails: {
                                  ...eq.droneDetails,
                                  registrationId: e.target.value,
                                },
                              },
                            };
                          })
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                        Pilot name
                      </FormLabel>
                      <Input
                        {...inputBaseProps}
                        value={normalizeEquipmentForState(form.equipment).droneDetails.pilotName || ""}
                        onChange={(e) =>
                          setForm((p) => {
                            const eq = normalizeEquipmentForState(p.equipment);
                            return {
                              ...p,
                              equipment: {
                                ...eq,
                                droneDetails: {
                                  ...eq.droneDetails,
                                  pilotName: e.target.value,
                                },
                              },
                            };
                          })
                        }
                      />
                    </FormControl>
                  </SimpleGrid>
                </CardBody>
              </Card>
            ) : null}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} alignItems="flex-start">
              <FormControl>
                <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                  Lighting load
                </FormLabel>
                <Select
                  {...selectBaseProps}
                  value={form.lighting}
                  onChange={(e) => setF({ lighting: e.target.value })}
                >
                  <option value="normal">Normal</option>
                  <option value="heavy">Heavy</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                  Generator on set
                </FormLabel>
                <Flex
                  align="center"
                  justify="space-between"
                  minH="12"
                  h="12"
                  px={4}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor="gray.200"
                  bg="white"
                >
                  <Text fontSize="sm" color="gray.600">
                    Needed at location?
                  </Text>
                  <Switch
                    {...switchVisibleProps}
                    isChecked={form.generator_required}
                    onChange={(e) => setF({ generator_required: e.target.checked })}
                  />
                </Flex>
              </FormControl>
            </SimpleGrid>
          </Stack>
        );
      case 7:
        return (
          <Stack spacing={8}>
            <FormControl isRequired>
              <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                Number of vehicles
              </FormLabel>
              <NumberInput
                value={numberInputValue(normalizeVehiclesForState(form.vehicles).count)}
                onChange={(_, v) =>
                  setForm((p) => {
                    const veh0 = normalizeVehiclesForState(p.vehicles);
                    return { ...p, vehicles: { ...veh0, count: v } };
                  })
                }
                min={0}
                size="lg"
              >
                <NumberInputField {...numberFieldProps} placeholder="0" />
              </NumberInput>
            </FormControl>
            <FormControl>
              <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={3}>
                Vehicle types
              </FormLabel>
              <Text fontSize="xs" color="gray.500" mb={2}>
                Tap options to select or clear — you can choose more than one.
              </Text>
              <Wrap spacing={3}>
                {VEHICLE_OPTS.map((t) => {
                  const selected = normalizeVehiclesForState(form.vehicles).types.includes(t);
                  return (
                    <WrapItem key={t}>
                      <Button
                        type="button"
                        size="md"
                        px={5}
                        py={6}
                        h="auto"
                        borderRadius="lg"
                        borderWidth="2px"
                        fontWeight="semibold"
                        variant="outline"
                        borderColor={selected ? "gray.700" : "gray.300"}
                        bg={selected ? "gray.700" : "white"}
                        color={selected ? "gray.100" : "gray.800"}
                        _hover={{
                          bg: selected ? "gray.600" : "gray.50",
                          borderColor: "gray.500",
                        }}
                        onClick={() => {
                          setForm((p) => {
                            const v0 = normalizeVehiclesForState(p.vehicles);
                            const cur = v0.types;
                            const next = cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t];
                            return { ...p, vehicles: { ...v0, types: next } };
                          });
                        }}
                      >
                        {t}
                      </Button>
                    </WrapItem>
                  );
                })}
              </Wrap>
            </FormControl>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Flex
                align="center"
                justify="space-between"
                p={4}
                borderRadius="xl"
                borderWidth="1px"
                borderColor="gray.100"
                bg="gray.50"
              >
                <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={0}>
                  Parking required
                </FormLabel>
                <Switch
                  {...switchVisibleProps}
                  isChecked={normalizeVehiclesForState(form.vehicles).parkingRequired}
                  onChange={(e) =>
                    setForm((p) => {
                      const veh0 = normalizeVehiclesForState(p.vehicles);
                      return { ...p, vehicles: { ...veh0, parkingRequired: e.target.checked } };
                    })
                  }
                />
              </Flex>
              <Flex
                align="center"
                justify="space-between"
                p={4}
                borderRadius="xl"
                borderWidth="1px"
                borderColor="gray.100"
                bg="gray.50"
              >
                <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={0}>
                  Entry permission needed
                </FormLabel>
                <Switch
                  {...switchVisibleProps}
                  isChecked={normalizeVehiclesForState(form.vehicles).entryPermission}
                  onChange={(e) =>
                    setForm((p) => {
                      const veh0 = normalizeVehiclesForState(p.vehicles);
                      return { ...p, vehicles: { ...veh0, entryPermission: e.target.checked } };
                    })
                  }
                />
              </Flex>
            </SimpleGrid>
          </Stack>
        );
      case 8:
        return (
          <Stack spacing={3}>
            {Object.entries({
              loudSound: "Loud sound / music",
              roadBlocking: "Road blocking",
              crowdControl: "Crowd control",
              stunts: "Stunts / action",
              fireExplosives: "Fire / explosives",
              animals: "Animals",
            }).map(([k, label]) => (
              <Flex
                key={k}
                align="center"
                justify="space-between"
                gap={4}
                p={4}
                borderRadius="xl"
                borderWidth="1px"
                borderColor="gray.100"
                bg="white"
                transition="box-shadow 0.15s ease"
                _hover={{ boxShadow: "sm" }}
              >
                <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={0}>
                  {label}
                </FormLabel>
                <Switch
                  {...switchVisibleProps}
                  isChecked={!!form.special_requirements?.[k]}
                  onChange={(e) =>
                    setF({
                      special_requirements: { ...form.special_requirements, [k]: e.target.checked },
                    })
                  }
                />
              </Flex>
            ))}
            {form.special_requirements?.fireExplosives ? (
              <Flex
                align="flex-start"
                gap={3}
                bg="orange.50"
                borderWidth="1px"
                borderColor="orange.100"
                p={4}
                borderRadius="xl"
                color="orange.900"
              >
                <Icon as={FiAlertTriangle} boxSize={5} mt={0.5} flexShrink={0} />
                <Text fontSize="sm" fontWeight="medium" lineHeight="tall">
                  Pyrotechnics require extra clearances — authorities may need additional NOCs.
                </Text>
              </Flex>
            ) : null}
          </Stack>
        );
      case 9:
        return (
          <Stack spacing={6}>
            <Flex
              align="center"
              justify="space-between"
              p={5}
              borderRadius="xl"
              borderWidth="1px"
              borderColor="gray.100"
              bg="gray.50"
            >
              <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={0}>
                Electricity at location
              </FormLabel>
              <Switch
                {...switchVisibleProps}
                size="lg"
                isChecked={form.utilities?.electricity}
                onChange={(e) => setF({ utilities: { ...form.utilities, electricity: e.target.checked } })}
              />
            </Flex>
            <FormControl>
              <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                Generator usage
              </FormLabel>
              <Input
                {...inputBaseProps}
                placeholder="e.g. 5 KVA × 2"
                value={form.utilities?.generatorUsage || ""}
                onChange={(e) => setF({ utilities: { ...form.utilities, generatorUsage: e.target.value } })}
              />
            </FormControl>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
              <FormControl>
                <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                  Water requirement
                </FormLabel>
                <Select
                  {...selectBaseProps}
                  placeholder="Select level"
                  value={form.utilities?.water ?? ""}
                  onChange={(e) => setF({ utilities: { ...form.utilities, water: e.target.value } })}
                >
                  <option value="none">None</option>
                  <option value="drinking">Drinking only</option>
                  <option value="production">Production use</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
                  Washroom setup
                </FormLabel>
                <Select
                  {...selectBaseProps}
                  placeholder="Select option"
                  value={form.utilities?.washroom ?? ""}
                  onChange={(e) => setF({ utilities: { ...form.utilities, washroom: e.target.value } })}
                >
                  <option value="existing">Use existing</option>
                  <option value="portable">Portable units</option>
                </Select>
              </FormControl>
            </SimpleGrid>
          </Stack>
        );
      case 10:
        return (
          <Box
            borderRadius="2xl"
            borderWidth="1px"
            borderColor="indigo.100"
            bg="linear-gradient(145deg, #f8fafc 0%, #eef2ff 45%, #faf5ff 100%)"
            p={{ base: 4, md: 6 }}
          >
            <Heading size="sm" color="gray.800" mb={1}>
              Legal documents
            </Heading>
            <Text fontSize="sm" color="gray.600" mb={6}>
              Bond, insurance, and undertaking must be on file before submission.
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {renderUploadRow("Indemnity bond", "legal.indemnity_bond")}
              {renderUploadRow("Insurance certificate (mandatory)", "legal.insurance_certificate")}
              {renderUploadRow("Undertaking form", "legal.undertaking_form")}
            </SimpleGrid>
          </Box>
        );
      case 11:
        return (
          <Stack spacing={8}>
            {renderUploadRow("Previous permissions (optional)", "final.previous_permissions")}
            <Divider borderColor="gray.200" />
            <Box
              borderRadius="xl"
              borderWidth="1px"
              borderColor="gray.100"
              bg="gray.50"
              p={{ base: 5, md: 6 }}
            >
              <Text fontSize="xs" fontWeight="semibold" color="indigo.600" textTransform="uppercase" letterSpacing="0.08em" mb={4}>
                Application summary
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {[
                  ["Title", form.project_title || "—"],
                  ["Type", form.project_type || "—"],
                  ["Location", form.location_name || "—"],
                  ["Schedule", form.total_days ? `${form.total_days} days` : "—"],
                  ["Request ID", requestRef || "—"],
                ].map(([k, v]) => (
                  <Box key={k}>
                    <Text fontSize="xs" color="gray.500" fontWeight="medium">
                      {k}
                    </Text>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.900" mt={1}>
                      {v}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
            <Button
              size="lg"
              h="14"
              borderRadius="xl"
              fontSize="md"
              fontWeight="semibold"
              rightIcon={<Icon as={FiChevronRight} color="gray.800" />}
              onClick={submitFinal}
              boxShadow="0 4px 14px rgba(0, 0, 0, 0.12)"
              {...primaryButtonProps}
            >
              Submit application
            </Button>
          </Stack>
        );
      default:
        return null;
    }
  };

  const mainBottomPad = useBreakpointValue({
    base: "calc(76px + 72px + env(safe-area-inset-bottom, 0px) + 24px)",
    md: "120px",
  });

  return (
    <Box
      bg="gray.50"
      minH="100vh"
      pb={mainBottomPad ?? "120px"}
      px={{ base: 3, md: 4 }}
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: "absolute",
        inset: 0,
        height: "320px",
        bgGradient: "linear(to-b, indigo.50, transparent)",
        pointerEvents: "none",
        zIndex: 0,
      }}
    >
      <Header />
      <Container maxW="container.md" py={{ base: 6, md: 10 }} position="relative" zIndex={1}>
        <VStack align="stretch" spacing={{ base: 6, md: 8 }}>
          <Box
            borderRadius="2xl"
            bg="white"
            borderWidth="1px"
            borderColor="gray.100"
            p={{ base: 5, md: 7 }}
            boxShadow="0 4px 24px rgba(15, 23, 42, 0.06)"
          >
            <Flex justify="space-between" align="flex-start" gap={4} flexWrap="wrap">
              <Box>
                <Text fontSize="xs" fontWeight="semibold" color="indigo.600" textTransform="uppercase" letterSpacing="0.1em" mb={2}>
                  Smart application
                </Text>
                <Heading size={{ base: "lg", md: "xl" }} letterSpacing="-0.03em" color="gray.900">
                  Location permission
                </Heading>
                {/* <Text color="gray.600" fontSize="sm" mt={3} maxW="lg" lineHeight="tall">
                  Guided steps with autosave.
                </Text> */}
              </Box>
              {requestRef ? (
                <Badge
                  colorScheme="gray"
                  variant="subtle"
                  fontFamily="mono"
                  fontSize="xs"
                  px={3}
                  py={2}
                  borderRadius="lg"
                  alignSelf="flex-start"
                >
                  {requestRef}
                </Badge>
              ) : null}
            </Flex>
          </Box>

          <Card
            ref={formCardRef}
            shadow="xl"
            borderRadius="2xl"
            borderWidth="1px"
            borderColor="gray.100"
            overflow="hidden"
            bg="white"
          >
            <Box h="4px" bgGradient="linear(to-r, indigo.500, violet.500, fuchsia.500)" />
            <CardHeader pb={4} pt={{ base: 6, md: 8 }} px={{ base: 4, md: 8 }}>
              <StepProgressHeader step={step} total={STEP_LABELS.length} />
            </CardHeader>
            <Divider borderColor="gray.100" />
            <CardBody px={{ base: 4, md: 8 }} py={{ base: 6, md: 8 }} pb={{ base: 8, md: 10 }}>
              {stepBody()}
            </CardBody>
          </Card>

          {saving ? (
            <HStack justify="center" spacing={2} color="gray.500" fontSize="sm">
              <Spinner size="sm" color="indigo.400" />
              <Text>Saving draft…</Text>
            </HStack>
          ) : null}
        </VStack>
      </Container>

      <Box
        position="fixed"
        bottom={footerBottom ?? 0}
        left={0}
        right={0}
        bg="rgba(255, 255, 255, 0.88)"
        backdropFilter="blur(12px)"
        borderTop="1px solid"
        borderColor="gray.200"
        py={3}
        px={4}
        zIndex={footerZ ?? 1002}
        boxShadow="0 -8px 32px rgba(15, 23, 42, 0.08)"
        sx={{ pb: "max(12px, env(safe-area-inset-bottom, 0px))" }}
      >
        <Container maxW="container.md" px={{ base: 2, md: 4 }}>
          <Flex justify="space-between" align="center" gap={3} flexWrap="wrap">
            <Button
              flex={{ base: 1, md: "unset" }}
              minW={{ base: "44%", md: "auto" }}
              variant="outline"
              size={{ base: "lg", md: "md" }}
              borderRadius="xl"
              borderWidth="2px"
              borderColor="gray.400"
              color="gray.800"
              bg="white"
              fontWeight="semibold"
              _hover={{ bg: "gray.50", borderColor: "gray.500" }}
              onClick={goBack}
              isDisabled={step === 0}
            >
              Back
            </Button>
            {step < STEP_LABELS.length - 1 ? (
              <Button
                flex={{ base: 1, md: "unset" }}
                minW={{ base: "44%", md: "auto" }}
                size={{ base: "lg", md: "md" }}
                borderRadius="xl"
                fontWeight="semibold"
                rightIcon={<Icon as={FiChevronRight} color="gray.800" />}
                {...primaryButtonProps}
                onClick={goNext}
              >
                Continue
              </Button>
            ) : null}
          </Flex>
        </Container>
      </Box>
    </Box>
  );
}
